import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { Layout } from '../components/Layout';
import { ArticleCard, Article } from '../components/ArticleCard';
import { StatusBadge } from '../components/StatusBadge';
import { apiFetch } from '../lib/api';
import { titleOf } from '../lib/url';

// Library is the all-roads screen: your reading history (archived + filed),
// searchable and sortable. Trash (soft-deleted) is a default-off filter, not a
// separate surface. Queue items (unread/reading) live in the Queue, not here.
type Scope = 'library' | 'trash';
type Sort = 'added' | 'lastAccessed';

const SORTS: { key: Sort; label: string }[] = [
  { key: 'added', label: 'Date added' },
  { key: 'lastAccessed', label: 'Last accessed' },
];

export default function LibraryPage() {
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<Scope>('library');
  const [sort, setSort] = useState<Sort>('added');
  const [search, setSearch] = useState('');

  const { data: articles = [], isLoading } = useQuery<Article[]>({
    queryKey: ['articles', 'library', scope, sort],
    queryFn: () =>
      scope === 'trash'
        ? apiFetch('/articles?status=deleted')
        : apiFetch(`/articles?status=archived,filed&sort=${sort}`),
  });

  const restore = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/articles/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'unread' }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['articles'] }),
  });

  const filtered = articles.filter(
    (a) => !search || titleOf(a).toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-white mb-6">Library</h1>

        {/* Controls: scope (Library / Trash) + sort */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {(['library', 'trash'] as Scope[]).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                scope === s ? 'bg-violet-600 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {s}
            </button>
          ))}

          {scope === 'library' && (
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as Sort)}
              className="ml-auto px-3 py-1.5 rounded-lg text-sm text-text-primary bg-surface-1 border border-border-subtle focus:border-prism-violet focus:outline-none"
            >
              {SORTS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles…"
            className="w-full px-3 py-2 rounded-lg text-sm text-text-primary bg-surface-1 border border-border-subtle focus:border-prism-violet focus:outline-none transition-colors"
          />
        </div>

        {/* List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-text-muted">
            {scope === 'trash' ? (
              <p className="text-sm">Trash is empty.</p>
            ) : articles.length === 0 ? (
              <>
                <p className="text-sm font-medium text-text-secondary">Articles you finish land here</p>
                <p className="text-xs mt-1.5">Your library is your reading history — everything you’ve archived or filed.</p>
              </>
            ) : (
              <p>No articles match your search.</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((a) => (
              <div key={a.id} className="flex items-center gap-3 rounded-lg border border-white/5 p-3 bg-surface-1">
                <div className="flex-1 min-w-0">
                  <ArticleCard article={a} />
                </div>
                {scope === 'trash' ? (
                  <button
                    onClick={() => restore.mutate(a.id)}
                    disabled={restore.isPending}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-violet-300 border border-violet-500/30 hover:bg-violet-500/10 transition-colors"
                  >
                    <RotateCcw size={14} /> Restore
                  </button>
                ) : (
                  <StatusBadge status={a.status} />
                )}
                <span className="text-xs flex-shrink-0 text-text-muted">
                  {new Date(a.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
